import type { ContactFieldDto, ContactSortDto, ContactViewDto } from '@carbonwebmail/contracts'
import { count } from '../../ui/format.js'
import type { Rule } from '../segments/ruleVocabulary.js'
import type { KeyedRule } from '../segments/useSegmentBuilder.js'
import { BoardFilter } from './BoardFilter.js'
import { BoardTools } from './BoardTools.js'
import type { BoardColumn } from './boardColumns.js'

/**
 * The strip above the rows: what is on screen, and everything that changes it.
 *
 * **One row, and it scrolls rather than wraps.** It wrapped onto two lines at
 * ordinary widths, which put the two buttons under the count and made the
 * toolbar read as two unrelated strips. A board's tools are one thing; below
 * the width they fit in, the row slides sideways instead of stacking.
 *
 * Its own file because `ContactsList` was doing too much to read — the tools,
 * the four dialogs, the selection and the table. This is the half that has no
 * state of its own: everything here is handed in and handed back.
 */
export function BoardToolbar({
  search,
  onSearch,
  fields,
  columns,
  rules,
  sort,
  hidden,
  shown,
  onNewRule,
  onRules,
  onSort,
  onHidden,
  onSaveView,
  onDuplicates,
  onFixNames,
  onNewContact,
  mayWrite,
}: {
  /**
   * Whether to draw the three buttons that change the board.
   *
   * Handed in like everything else here rather than read from context, because
   * this file's whole point is that it holds nothing of its own — searching,
   * filtering and choosing columns are reads and stay for every role.
   */
  readonly mayWrite: boolean
  readonly search: string
  readonly onSearch: (value: string) => void
  readonly fields: readonly ContactFieldDto[]
  readonly columns: readonly BoardColumn[]
  readonly rules: readonly KeyedRule[]
  readonly sort: ContactSortDto | undefined
  readonly hidden: ReadonlySet<string>
  /** How many rows, and of what. Absent until the first answer arrives. */
  readonly shown: { readonly total: number; readonly view: ContactViewDto } | undefined
  readonly onNewRule: (rule: Rule) => KeyedRule
  readonly onRules: (rules: readonly KeyedRule[]) => void
  readonly onSort: (sort: ContactSortDto | undefined) => void
  readonly onHidden: (hidden: ReadonlySet<string>) => void
  readonly onSaveView: () => void
  readonly onDuplicates: () => void
  readonly onFixNames: () => void
  readonly onNewContact: () => void
}) {
  /*
   * The strip wraps rather than scrolls.
   *
   * It was `overflow-x-auto`, which makes the header a scrolling box — and a box
   * that scrolls on one axis clips the other, so "4,032 in total" lost its
   * descenders and the strip grew a scrollbar nobody asked for. Wrapping puts
   * the controls on a second line when they do not fit, which is what they
   * should do.
   */
  return (
    <header className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <div className="flex-none">
        {shown === undefined ? null : <Counted {...shown} search={search} />}
      </div>

      <label className="sr-only" htmlFor="contact-search">
        Search contacts
      </label>
      <span className="flex flex-wrap items-center gap-2">
        <input
          className="w-44 rounded-field border border-line bg-surface px-3 py-1.5 text-[13px] text-t1 lg:w-64"
          id="contact-search"
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Search contacts"
          type="search"
          value={search}
        />
        <BoardFilter
          fields={fields}
          onNewRule={onNewRule}
          onRules={onRules}
          // Saves a view of this board, which is what the tabs are. It used to
          // hand the rules to the segment builder, so narrowing a list and
          // keeping it created an audience a campaign could be sent to.
          onSaveAsView={onSaveView}
          rules={rules}
        />
        <BoardTools
          columns={columns}
          hidden={hidden}
          onHidden={onHidden}
          onSort={onSort}
          sort={sort}
        />
        {!mayWrite ? null : (
          <>
            {/*
          A button rather than a tab. Looking for duplicates is something
          somebody does to the board occasionally and then leaves; a permanent
          tab spent the width of the strip on it and put it beside the views,
          which are what the board is for.
        */}
            <button
              className="flex-none whitespace-nowrap rounded-pill border border-line px-4 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1"
              onClick={onDuplicates}
              type="button"
            >
              Duplicates
            </button>
            {/*
          Beside Duplicates because it is the same kind of errand: something
          done to the board once, after an import left thousands of rows
          reading the same word, and then forgotten about.
        */}
            <button
              className="flex-none whitespace-nowrap rounded-pill border border-line px-4 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1"
              onClick={onFixNames}
              type="button"
            >
              Fix names
            </button>
            {/*
          A dialog rather than a screen at `/contacts/new`. Adding a row to the
          board meant leaving the board, and coming back to find it exactly as
          it was — the thing somebody came to see is the new contact in the
          table.
        */}
            <button
              className="flex-none whitespace-nowrap rounded-pill bg-brand px-4 py-1.5 font-bold text-[13px] text-brand-contrast"
              onClick={onNewContact}
              type="button"
            >
              + New contact
            </button>
          </>
        )}
      </span>
    </header>
  )
}

/** How many, and of what — "matching" and "in this view" are different claims. */
function Counted({
  total,
  view,
  search,
}: {
  readonly total: number
  readonly view: ContactViewDto
  readonly search: string
}) {
  const of = view.kind === 'view' ? ` in ${view.name}` : search === '' ? ' in total' : ' matching'

  return (
    <p className="mt-1 text-[13px] text-t2">
      <span className="font-num tabular-nums">{count(total)}</span>
      {of}
    </p>
  )
}
